import React, { type PropsWithChildren } from 'react';
import { DesignSystemShadowProvider } from './DesignSystemShadowProvider';
import { RegionCurrencyContextProvider, WishlistCustomerKeyProvider } from '@skin-seoul/service';
import { QueryClient } from '@tanstack/react-query';
import { QueryClientProvider } from '@tanstack/react-query';
import { LoggerProvider } from '../utils/logger/Logger';
import { PageLogger } from '../utils/logger/hooks/usePageLogger';

interface ShortcodeProviderProps {
  children: React.ReactNode;
}

const queryClient = new QueryClient();

/**
 * Main provider that wraps all shortcode components with necessary context providers
 *
 * @example
 * ```tsx
 * <ShortcodeProvider>
 *   <YourShortcodeComponent />
 * </ShortcodeProvider>
 * ```
 */
export function ShortcodeProvider({ children }: PropsWithChildren<ShortcodeProviderProps>) {
  const isDebugMode = process.env.NODE_ENV === 'development';

  return (
    <DesignSystemShadowProvider>
      <QueryClientProvider client={queryClient}>
        <RegionCurrencyContextProvider>
          <WishlistCustomerKeyProvider>
            <LoggerProvider appName="SkinSeoulShortcode" debug={isDebugMode}>
              <PageLogger pageName="ReactShortcode">{children}</PageLogger>
            </LoggerProvider>
          </WishlistCustomerKeyProvider>
        </RegionCurrencyContextProvider>
      </QueryClientProvider>
    </DesignSystemShadowProvider>
  );
}
